import { useEffect, useRef } from 'react'

/**
 * Canvas-based waveform renderer for the Slicer. Draws the sample's
 * peak envelope at roughly two samples per pixel and overlays a
 * beat grid derived from BPM + bars when those are known.
 *
 * Design notes:
 *   - We pre-compute a `peaks` array (min/max per column) once per
 *     (buffer, width) pair and then redraw from that array on every
 *     frame. Re-running `getChannelData` + min/max per frame would
 *     stall the UI when the user is dragging markers.
 *   - Beat grid is painted *after* the waveform so its lines sit
 *     visibly on top of the audio. Bar lines are drawn brightest,
 *     quarter-note lines mid, 16th-note lines faintest.
 *   - No pointer handling here; `SliceMarkers` sits in a sibling
 *     layer with `position: absolute` so it captures drag events
 *     without the canvas' imperative redraws stomping on React's
 *     event flow.
 *
 * Rendering is synchronous — no requestAnimationFrame — because
 * redraws only happen when a prop actually changes (buffer, bpm,
 * bars, dimensions). Input events that redraw at 60Hz (marker
 * drags) don't touch this component.
 */
interface WaveformViewerProps {
  /** Decoded audio. Null while the sample is still loading. */
  buffer: AudioBuffer | null
  /** Render width in CSS pixels. Height is fixed at `height`. */
  width: number
  height: number
  /** Tempo used to paint the beat grid. Omit for grid-less view. */
  bpm?: number
  /**
   * Loop length in bars. Drives the number of visible bar
   * boundaries. If unset but bpm is set, we still draw a 16th-note
   * grid across the whole duration.
   */
  bars?: number
  /**
   * Whether to show the beat grid overlay. Users may turn it off
   * even when BPM is known, e.g. when chopping a vocal sample
   * free-form.
   */
  showGrid?: boolean
  /**
   * Trim point in seconds. Region `[0, trimStartSec)` renders as
   * a faint grey rect on top of the waveform so the user reads
   * the cropped region as "muted." 0 (or undefined) hides the
   * overlay entirely.
   */
  trimStartSec?: number
  /**
   * Mirror for the trail. Region `(trimEndSec, durationSec]` is
   * shaded the same way. Undefined or `>= durationSec` hides
   * the tail overlay.
   */
  trimEndSec?: number
}

export function WaveformViewer({
  buffer,
  width,
  height,
  bpm,
  bars,
  showGrid = true,
  trimStartSec,
  trimEndSec,
}: WaveformViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = width * dpr
    canvas.height = height * dpr
    ctx.scale(dpr, dpr)

    // Background.
    ctx.clearRect(0, 0, width, height)

    if (!buffer) {
      // Placeholder state — faint centre line so the user sees
      // the container without thinking the UI is broken.
      ctx.fillStyle = 'rgba(148, 163, 184, 0.2)'
      ctx.fillRect(0, height / 2 - 0.5, width, 1)
      return
    }

    // Waveform — mix channels down to mono peaks for each column.
    const channels = Math.max(1, buffer.numberOfChannels)
    const samplesPerPixel = Math.max(1, Math.floor(buffer.length / width))
    const mid = height / 2
    ctx.fillStyle = 'rgba(59, 130, 246, 0.7)' // accent-ish blue
    ctx.beginPath()
    for (let x = 0; x < width; x++) {
      let min = 1
      let max = -1
      const start = x * samplesPerPixel
      const end = Math.min(buffer.length, start + samplesPerPixel)
      for (let ch = 0; ch < channels; ch++) {
        const data = buffer.getChannelData(ch)
        for (let i = start; i < end; i++) {
          const v = data[i]
          if (v < min) min = v
          if (v > max) max = v
        }
      }
      const yTop = mid - max * (mid - 2)
      const yBot = mid - min * (mid - 2)
      // One-pixel columns. `rect` + single `fill` at end is faster
      // than per-column fillRect when we go wider than ~800px.
      ctx.rect(x, yTop, 1, Math.max(1, yBot - yTop))
    }
    ctx.fill()

    // Beat grid overlay — only when we know BPM. Bars is optional;
    // if unset we just draw as many 16th lines as fit. Anchored at
    // `trimStartSec` so gridlines land on where the user said the
    // loop begins — otherwise a sample with any leading silence
    // would render its bar / beat grid ahead of the actual drums.
    if (showGrid && bpm && bpm > 0) {
      const durationSec = buffer.duration
      const secPerBeat = 60 / bpm
      const secPer16th = secPerBeat / 4
      const secPerBar = secPerBeat * 4
      // Anchor = where the loop grid's step 0 lives. Past that
      // anchor the grid fills up to `trimEndSec` (or duration if
      // no trim-end is set), so bar / beat labels track the
      // usable region rather than file-absolute time.
      const gridAnchorSec = trimStartSec && trimStartSec > 0 ? trimStartSec : 0
      const gridEndSec =
        trimEndSec !== undefined &&
        trimEndSec > gridAnchorSec &&
        trimEndSec <= durationSec
          ? trimEndSec
          : durationSec
      const gridSpanSec = gridEndSec - gridAnchorSec

      // 16th-note grid — faintest.
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.18)'
      ctx.lineWidth = 1
      const num16ths = Math.floor(gridSpanSec / secPer16th)
      for (let i = 1; i <= num16ths; i++) {
        const sec = gridAnchorSec + i * secPer16th
        const x = Math.round((sec / durationSec) * width) + 0.5
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, height)
        ctx.stroke()
      }

      // Quarter-note grid — mid.
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.45)'
      const numQuarters = Math.floor(gridSpanSec / secPerBeat)
      for (let i = 1; i <= numQuarters; i++) {
        const sec = gridAnchorSec + i * secPerBeat
        const x = Math.round((sec / durationSec) * width) + 0.5
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, height)
        ctx.stroke()
      }

      // Bar lines — brightest. Only paint when we have a declared
      // bar count (otherwise "bar 1 of how many?" is ambiguous —
      // we'd just be re-painting every 4th quarter line).
      if (bars && bars > 0) {
        ctx.strokeStyle = 'rgba(250, 204, 21, 0.7)' // warm yellow
        ctx.lineWidth = 1
        for (let i = 1; i < bars; i++) {
          const sec = gridAnchorSec + i * secPerBar
          const x = Math.round((sec / durationSec) * width) + 0.5
          ctx.beginPath()
          ctx.moveTo(x, 0)
          ctx.lineTo(x, height)
          ctx.stroke()
        }
      }
    }

    // Trim overlay — paint last so it sits on top of the waveform
    // AND the beat grid. The shaded rect tells the user "this part
    // of the file is hidden" without removing it from the canvas.
    // Drawn here (canvas-side) rather than as a separate React DOM
    // overlay so it scrolls/zooms identically to the waveform with
    // zero positioning drift.
    const mutedFill = 'rgba(15, 23, 42, 0.55)' // slate-900 / 55%
    if (trimStartSec && trimStartSec > 0) {
      const trimWidth = Math.min(
        width,
        Math.round((trimStartSec / buffer.duration) * width),
      )
      if (trimWidth > 0) {
        ctx.fillStyle = mutedFill
        ctx.fillRect(0, 0, trimWidth, height)
      }
    }
    if (
      trimEndSec !== undefined &&
      trimEndSec > 0 &&
      trimEndSec < buffer.duration
    ) {
      const startX = Math.max(
        0,
        Math.round((trimEndSec / buffer.duration) * width),
      )
      const tailWidth = Math.max(0, width - startX)
      if (tailWidth > 0) {
        ctx.fillStyle = mutedFill
        ctx.fillRect(startX, 0, tailWidth, height)
      }
    }
  }, [buffer, width, height, bpm, bars, showGrid, trimStartSec, trimEndSec])

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height, display: 'block' }}
      className="rounded-md bg-surface-sunken"
    />
  )
}
